import { type ReactNode } from 'react'
import { Page, TSection } from '@components'
import { Size, useUpdateGame } from '@front/model'
import { useToastError } from '@hooks'
import { Stack } from '@mantine/core'
import { isNotEmpty, type GameCriteria } from '@terros/common'
import { CompetitionPageContainer } from '../competition/CompetitionPageContainer'
import { useGameContext } from '../competition_.$gameId/useGameContext'
import { getGameMetadataTitle } from '../competition_.$gameId/util'
import { ScoringMetricsTable } from './ScoringMetricsTable'
import { NoScoringMetrics } from './components/NoScoringMetrics'
import { Tiebreakers } from './tiebreaker'
import { createFileRoute } from '@tanstack/react-router'

function CompetitionScoring(): ReactNode {
  const { data, refetch, isFetching } = useGameContext()

  const criteria = data.game.criteria
  const { update, error } = useUpdateGame()
  useToastError(error)

  const handleCriteriaChange = async (criteria: GameCriteria[]): Promise<void> => {
    await update({
      ...data.game,
      criteria,
    })
  }

  const handleFirstCriteriaAdded = async (criteria: GameCriteria): Promise<void> => {
    await handleCriteriaChange([criteria])
  }

  const criteriaExist = isNotEmpty(data.game.criteria)

  return (
    <CompetitionPageContainer>
      <Page.Heading
        isFetching={isFetching}
        metadata={{ title: getGameMetadataTitle(data.game, 'Scoring') }}
        refetch={refetch}
        title='Scoring Metrics'
      />
      {!criteriaExist && <NoScoringMetrics onScoringMetricAdded={handleFirstCriteriaAdded} />}
      {criteriaExist && (
        <Page.Content scrollArea>
          <Stack gap={Size.space['5xl']}>
            <TSection heading={{ title: 'Scoring Metrics' }}>
              <ScoringMetricsTable criteria={criteria} handleCriteriaChange={handleCriteriaChange} />
            </TSection>
            <Tiebreakers />
          </Stack>
        </Page.Content>
      )}
    </CompetitionPageContainer>
  )
}

export const Route = createFileRoute('/competition_/$gameId/scoring/')({
  component: CompetitionScoring,
})
